import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Event.module.css';
import { apiUrl } from '../config/api';
import { useInView } from 'react-intersection-observer';

interface EventItem {
  id: number;
  title: string;
  image: string;
  image_url: string;
  created_at?: string;
  updated_at?: string;
}

const Event: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(3);
  const [isMobile, setIsMobile] = useState(false);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // triggerOnce: false -> animation replays every time you scroll it into view
  const { ref: headerRef, inView: headerInView } = useInView({ threshold: 0.2, triggerOnce: false });
  const { ref: cardsRef, inView: cardsInView } = useInView({ threshold: 0.1, triggerOnce: false });

  // Fetch events from Laravel API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        console.log('Fetching events from API...');
        const response = await fetch(apiUrl('/api/events'));

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        console.log('API Response:', result);

        if (result.success) {
          setEvents(result.data);
        } else {
          setError('API returned unsuccessful response');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(`Failed to fetch events: ${errorMessage}`);
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Handle responsive slides
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSlidesToShow(1);
        setIsMobile(true);
      } else if (window.innerWidth <= 1024) {
        setSlidesToShow(2);
        setIsMobile(false);
      } else {
        setSlidesToShow(3);
        setIsMobile(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset position whenever the visible card count changes (resize), so the
  // track never ends up translated past its new valid range.
  useEffect(() => {
    setCurrentSlide(0);
  }, [slidesToShow]);

  // totalSlides = number of valid track positions. With a continuous track,
  // each "slide" moves the window by exactly one card.
  const maxIndex = Math.max(events.length - slidesToShow, 0);
  const totalSlides = events.length > 0 ? maxIndex + 1 : 0;

  const nextSlide = () => {
    if (totalSlides === 0) return;
    setCurrentSlide((prev) =>
      prev === totalSlides - 1 ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    if (totalSlides === 0) return;
    setCurrentSlide((prev) =>
      prev === 0 ? totalSlides - 1 : prev - 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Auto-advance carousel every 4 seconds — moves ONE card at a time
  useEffect(() => {
    if (totalSlides <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
    }, 4000);

    return () => clearInterval(interval);
  }, [totalSlides]);

  const handleEventClick = (event: EventItem) => {
    navigate(`/event/${event.id}`, {
      state: {
        eventData: event
      }
    });
  };

  // Show loading state
  if (loading) {
    return (
      <section className={styles.eventSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.titleAnimation}>Our Events</h2>
          </div>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading events from database...</p>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className={styles.eventSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.titleAnimation}>Our Events</h2>
          </div>
          <div className={styles.errorState}>
            <p className={styles.errorMessage}>⚠️ {error}</p>
          </div>
        </div>
      </section>
    );
  }

  // Show empty state
  if (events.length === 0) {
    return (
      <section className={styles.eventSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.titleAnimation}>Our Events</h2>
          </div>
          <div className={styles.emptyState}>
            <p>No events available. Check back soon!</p>
          </div>
        </div>
      </section>
    );
  }

  // Percentage width of one card, used to shift the track by exactly one card
  const cardStepPercent = 100 / slidesToShow;

  return (
    <section className={styles.eventSection}>
      <div className={styles.container}>
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`${styles.sectionHeader} ${headerInView ? styles.animateHeader : ''}`}
        >
          <h2 className={styles.scrollTitle}>
            News & Events
            <span className={styles.titleUnderline}></span>
          </h2>
        </div>

        {/* Events Display */}
        <div ref={cardsRef} className={styles.carouselContainer}>
          <button className={styles.carouselBtn} onClick={prevSlide}>
            <i className="fas fa-chevron-left"></i>
          </button>

          <div className={styles.carouselTrack}>
            {/* Single continuous track — advancing shifts by ONE card, not a full group */}
            <div
              className={styles.eventsGrid}
              style={{
                transform: isMobile
                  ? `translateY(calc(-${currentSlide * 100}%))`
                  : `translateX(calc(-${currentSlide * cardStepPercent}%))`
              }}
            >
              {events.map((event, idx) => (
                <div
                  key={event.id}
                  className={`${styles.col} ${cardsInView ? styles.animateCard : ''}`}
                  style={{ transitionDelay: cardsInView ? `${Math.min(idx, 5) * 60}ms` : '0ms' }}
                >
                  <div className={styles.eventCard}>
                    <div className={styles.eventImage}>
                      <img
                        src={event.image_url}
                        alt={event.title}
                        decoding="async"
                        loading="lazy"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          const filename = event.image?.split('/').pop() || '';

                          // Try multiple fallback URLs
                          const fallbacks = [
                            apiUrl(`/images/events/${filename}`),
                            apiUrl(`/storage/events/${filename}`),
                          ];

                          // Find first fallback that works
                          let fallbackIndex = 0;
                          const tryFallback = () => {
                            if (fallbackIndex < fallbacks.length) {
                              img.src = fallbacks[fallbackIndex];
                              fallbackIndex++;
                            } else {
                              // All fallbacks failed, show placeholder
                              const firstLetter = event.title?.charAt(0) || 'E';
                              img.src = `https://placehold.co/600x400/2563eb/white?text=${encodeURIComponent(firstLetter)}`;
                            }
                          };

                          // Override onerror for fallback attempts
                          img.onerror = tryFallback;
                          tryFallback();
                        }}
                      />
                    </div>

                    <div className={styles.eventContent}>
                      <div className={styles.eventTitle}>
                        <h5>{event.title}</h5>
                      </div>

                      <div className={styles.eventCta}>
                        <button
                          className={styles.readMoreBtn}
                          onClick={() => handleEventClick(event)}
                        >
                          Read More
                          <i className="fas fa-arrow-right"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button className={styles.carouselBtn} onClick={nextSlide}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        {/* Carousel Dots */}
        {totalSlides > 1 && (
          <div className={styles.carouselDots}>
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                className={`${styles.dot} ${currentSlide === index ? styles.activeDot : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        )}

        {/* Slide Indicator */}
        <div className={styles.slideIndicator}>
          <span>Slide {currentSlide + 1} of {totalSlides}</span>
        </div>

      </div>
    </section>
  );
};

export default Event;



































//well formated for Fr
// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import styles from './Event.module.css';

// import ittt1 from '../images/slider/slider5.jpg';
// import ittt2 from '../images/new_1.jpg';
// import ittt3 from '../images/new_9.jpg';
// import ittt4 from '../images/new_13.jpg';
// import ittt6 from '../images/ittt6.jpg';
// import ittt5 from '../images/ittt5.jpg';

// interface EventItem {
//   id: number;
//   title: string;
//   image: string;
// }

// const Event: React.FC = () => {
//   const navigate = useNavigate();
//   const [currentSlide, setCurrentSlide] = useState(0);
//   const [slidesToShow, setSlidesToShow] = useState(3);
//   const [isMobile, setIsMobile] = useState(false);

//   const events: EventItem[] = [
//     {
//       id: 1,
//       title: "Appreciating Members",
//       image: ittt2,
//     },
//     {
//       id: 2,
//       title: "Women & Children Empowerment",
//       image: ittt1,
//     },
//     {
//       id: 3,
//       title: "Appreciating Development Members",
//       image: ittt3,
//     },
//     {
//       id: 4,
//       title: "Signing a memorandum of understanding (MoU)",
//       image: ittt4,
//     },
//     {
//       id: 5,
//       title: "Graduating Students",
//       image: ittt5,
//     },
//     {
//       id: 6,
//       title: "Digital Skills Training",
//       image: ittt6,
//     }
//   ];

//   // Handle responsive slides
//   useEffect(() => {
//     const handleResize = () => {
//       if (window.innerWidth <= 768) {
//         setSlidesToShow(1); // Show 1 card at a time on mobile
//         setIsMobile(true);
//       } else if (window.innerWidth <= 1024) {
//         setSlidesToShow(2);
//         setIsMobile(false);
//       } else {
//         setSlidesToShow(3);
//         setIsMobile(false);
//       }
//     };

//     handleResize();
//     window.addEventListener('resize', handleResize);
    
//     return () => window.removeEventListener('resize', handleResize);
//   }, []);

//   // Calculate total slides (on mobile: 6 slides, on desktop: based on slidesToShow)
//   const totalSlides = isMobile ? events.length : Math.ceil(events.length / slidesToShow);

//   const nextSlide = () => {
//     setCurrentSlide((prev) => 
//       prev === totalSlides - 1 ? 0 : prev + 1
//     );
//   };

//   const prevSlide = () => {
//     setCurrentSlide((prev) => 
//       prev === 0 ? totalSlides - 1 : prev - 1
//     );
//   };

//   const goToSlide = (index: number) => {
//     setCurrentSlide(index);
//   };

//   const handleEventClick = (event: EventItem) => {
//     navigate(`/event/${event.id}`, { 
//       state: { 
//         eventData: event 
//       } 
//     });
//   };



//   return (
//     <section className={styles.eventSection}>
//       <div className={styles.container}>
//         {/* Section Header */}
//         <div className={styles.sectionHeader}>
//           <h2 className={styles.titleAnimation}>Our Events</h2>
//         </div>

//         {/* Events Display */}
//         <div className={styles.carouselContainer}>
//         {/* // In Event.tsx, update the button icons: */}
//         <button className={styles.carouselBtn} onClick={prevSlide}>
//           <i className="fas fa-chevron-left"></i> {/* Left arrow */}
//         </button>


//           <div className={styles.carouselTrack}>
//             <div className={styles.eventsGrid} style={{ 
//               transform: isMobile 
//                 ? `translateY(calc(-${currentSlide * 100}%))`
//                 : `translateX(calc(-${currentSlide * 100}%))`
//             }}>
//               {isMobile ? (
//                 // Mobile: All cards stacked vertically
//                 events.map((event) => (
//                   <div key={event.id} className={styles.col}>
//                     <div className={styles.eventCard}>
//                       <div className={styles.eventImage}>
//                         <img 
//                           src={event.image} 
//                           alt={event.title}
//                           decoding="async"
//                         />
//                       </div>
                      
//                       <div className={styles.eventContent}>
//                         <div className={styles.eventTitle}>
//                           <h5>{event.title}</h5>
//                         </div>
                        
//                         <div className={styles.eventCta}>
//                           <button 
//                             className={styles.readMoreBtn}
//                             onClick={() => handleEventClick(event)}
//                           >
//                             Read More
//                             <i className="fas fa-arrow-right"></i>
//                           </button>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 ))
//               ) : (
//                 // Desktop: 3 cards horizontal per slide
//                 Array.from({ length: totalSlides }).map((_, slideIndex) => (
//                   <div key={slideIndex} className={styles.slide}>
//                     {events.slice(slideIndex * slidesToShow, slideIndex * slidesToShow + slidesToShow).map((event) => (
//                       <div key={event.id} className={styles.col}>
//                         <div className={styles.eventCard}>
//                           <div className={styles.eventImage}>
//                             <img 
//                               src={event.image} 
//                               alt={event.title}
//                               decoding="async"
//                             />
//                           </div>
                          
//                           <div className={styles.eventContent}>
//                             <div className={styles.eventTitle}>
//                               <h5>{event.title}</h5>
//                             </div>
                            
//                             <div className={styles.eventCta}>
//                               <button 
//                                 className={styles.readMoreBtn}
//                                 onClick={() => handleEventClick(event)}
//                               >
//                                 Read More
//                                 <i className="fas fa-arrow-right"></i>
//                               </button>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 ))
//               )}
//             </div>
//           </div>

//           <button className={styles.carouselBtn} onClick={nextSlide}>
//             {/* right button carousel */}
//             <i className="fas fa-chevron-right"></i>
//           </button>
//         </div>

//         {/* Carousel Dots */}
//         <div className={styles.carouselDots}>
//           {Array.from({ length: totalSlides }).map((_, index) => (
//             <button
//               key={index}
//               className={`${styles.dot} ${currentSlide === index ? styles.activeDot : ''}`}
//               onClick={() => goToSlide(index)}
//             />
//           ))}
//         </div>

//         {/* Slide Indicator */}
//         <div className={styles.slideIndicator}>
//           <span>Event {currentSlide + 1} of {totalSlides}</span>
//           <span className={styles.eventsIndicator}>
//             {events[currentSlide]?.title}
//           </span>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default Event;






































// import React, { useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import styles from './Event.module.css';


// import ittt1 from '../images/slider/slider5.jpg';
// import ittt2 from '../images/new_1.jpg';
// import ittt3 from '../images/new_9.jpg';
// import ittt4 from '../images/new_13.jpg';
// import ittt6 from '../images/ittt6.jpg';
// import ittt5 from '../images/ittt5.jpg';

// interface EventItem {
//   id: number;
//   title: string;
//   image: string;

// }

// const Event: React.FC = () => {
//   const navigate = useNavigate();
//   const [currentSlide, setCurrentSlide] = useState(0);

//   const events: EventItem[] = [

//     {
//       id: 1,
//       title: "Appreciating Members",
//       image: ittt2,
//     },
//     {
//       id: 2,
//       title: "Women & Children Empowerment",
//       image: ittt1,
//     },
//     {
//       id: 3,
//       title: "Appreciating Development Members",
//       image: ittt3,

//     },
//     {
//       id: 4,
//       title: "Signing a memorandum of understanding (MoU)",
//       image: ittt4,
//     },
//     {
//       id: 5,
//       title: "Graduating Students",
//       image: ittt5,
//     },
//     {
//       id: 6,
//       title: "Digital Skills Training",
//       image: ittt6,
//     }
//   ];

//   const slidesToShow = 3;
//   const totalSlides = Math.ceil(events.length / slidesToShow);

//   const nextSlide = () => {
//     setCurrentSlide((prev) => (prev + 1) % totalSlides);
//   };

//   const prevSlide = () => {
//     setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
//   };

//   const goToSlide = (index: number) => {
//     setCurrentSlide(index);
//   };

//   const handleEventClick = (event: EventItem) => {
//     // Navigate to ReadMoreEvent with the event data
//     navigate(`/event/${event.id}`, { 
//       state: { 
//         eventData: event 
//       } 
//     });
//   };

//   const visibleEvents = events.slice(
//     currentSlide * slidesToShow,
//     (currentSlide + 1) * slidesToShow
//   );

//   return (
//     <section className={styles.eventSection}>
//       <div className={styles.container}>
//         {/* Section Header */}
//         <div className={styles.sectionHeader}>
//           <h2 className={styles.titleAnimation}>Our Events</h2>
//         </div>

//         {/* Events Carousel */}
//         <div className={styles.carouselContainer}>
//           <button className={styles.carouselBtn} onClick={prevSlide}>
//             <i className="fas fa-chevron-left"></i>
//           </button>

//           <div className={styles.carouselTrack}>
//             <div className={styles.eventsGrid}>
//               {visibleEvents.map((event) => (
//                 <div key={event.id} className={styles.col}>
//                   <div className={styles.eventCard}>
//                     <div className={styles.eventImage}>
//                       <img 
//                         src={event.image} 
//                         alt={event.title}
//                         decoding="async"
//                       />
//                     </div>
                    
//                     <div className={styles.eventContent}>

                      
//                       <div className={styles.eventTitle}>
//                         <h5>{event.title}</h5>
//                       </div>
                      
//                       <div className={styles.eventCta}>
//                         <button 
//                           className={styles.readMoreBtn}
//                           onClick={() => handleEventClick(event)}
//                         >
//                           Read More
//                           <i className="fas fa-arrow-right"></i>
//                         </button>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>

//           <button className={styles.carouselBtn} onClick={nextSlide}>
//             <i className="fas fa-chevron-right"></i>
//           </button>
//         </div>

//         {/* Carousel Dots */}
//         <div className={styles.carouselDots}>
//           {Array.from({ length: totalSlides }).map((_, index) => (
//             <button
//               key={index}
//               className={`${styles.dot} ${currentSlide === index ? styles.activeDot : ''}`}
//               onClick={() => goToSlide(index)}
//             />
//           ))}
//         </div>


//       </div>
//     </section>
//   );
// };

// export default Event;
























  // Auto-advance carousel every 4 seconds
// useEffect(() => {
//   if (totalSlides <= 1) return;

//   const interval = setInterval(() => {
//     setCurrentSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
//   }, 4000);

//   return () => clearInterval(interval);
// }, [totalSlides]);